import {
  addDays,
  addMinutes,
  endOfDay,
  getISODay,
  isValid,
  isWithinInterval,
  startOfDay,
} from 'date-fns';
import {
  TemporaryClosure,
  WorkingDays,
  TimeRange,
  Establishment,
  firestoreDateParser,
} from '@pedix-workspace/utils';

export type EstablishmentOpenedParams = {
  isOpened: boolean;
  isAboutToClose: boolean;
  isTemporaryClosed: boolean;
};

export type EstablishmentOpenedStatus = {
  isOpened: boolean;
  isAboutToClose: boolean;
  isTemporaryClosed: boolean;
  isInventoryBlocked: boolean;
  isClosed: boolean;
  isCartDisabled: boolean;
};

export const disableOrdersOnClosed = (workingDays?: WorkingDays) => {
  if (!workingDays || !workingDays.isActive) {
    return false;
  }
  return !workingDays.enableOrdersOnClosed;
};

export const isValidWorkingHours = (workingHours?: TimeRange) => {
  return workingHours && workingHours.from && workingHours.to;
};

export const getEstablishmentOpenedStatus = (
  { isOpened, isAboutToClose, isTemporaryClosed }: EstablishmentOpenedParams,
  workingDays: Establishment['workingDays'],
  isInventoryBlocked = false,
): EstablishmentOpenedStatus => {
  const isClosed = !isOpened;

  return {
    isOpened,
    isAboutToClose,
    isTemporaryClosed,
    isInventoryBlocked,
    isClosed: isClosed,
    isCartDisabled:
      isTemporaryClosed || isInventoryBlocked
        ? true
        : isClosed && disableOrdersOnClosed(workingDays),
  };
};

export type EstablishmentClosedReason = 'inventory' | 'temporary' | 'working-hours';

export const getEstablishmentClosedReason = (
  status: EstablishmentOpenedStatus,
): EstablishmentClosedReason | null => {
  if (!status.isCartDisabled) {
    return null;
  }
  if (status.isInventoryBlocked) {
    return 'inventory';
  }
  if (status.isTemporaryClosed) {
    return 'temporary';
  }
  return 'working-hours';
};

export type EstablishmentOrderingInput = Pick<
  Establishment,
  'workingDays' | 'temporaryClosure' | 'activeInventoryCount'
>;

export type EstablishmentOrderingStatus = EstablishmentOpenedStatus & {
  closedReason: EstablishmentClosedReason | null;
};

export const getEstablishmentOrderingStatus = (
  { workingDays, temporaryClosure, activeInventoryCount }: EstablishmentOrderingInput,
  dateReference: Date,
): EstablishmentOrderingStatus => {
  const status = getEstablishmentOpenedStatus(
    {
      isOpened: isEstablishmentOpened(workingDays, dateReference),
      isAboutToClose: isEstablishmentAboutToClose(workingDays, { dateReference }),
      isTemporaryClosed: isEstablishmentTemporaryClosed(temporaryClosure, { dateReference }),
    },
    workingDays,
    activeInventoryCount?.blockOrders ?? false,
  );

  return { ...status, closedReason: getEstablishmentClosedReason(status) };
};

export const isEstablishmentAboutToClose = (
  workingDays: WorkingDays | undefined,
  options: { timeOffsetInMinutes?: number; dateReference: Date | undefined },
) => {
  const { timeOffsetInMinutes = 15, dateReference = new Date() } = options;

  return (
    isEstablishmentOpened(workingDays, dateReference) &&
    !isEstablishmentOpened(workingDays, addMinutes(dateReference, timeOffsetInMinutes))
  );
};

export const isEstablishmentOpened = (
  workingDays: WorkingDays | undefined,
  dateReference?: Date,
): boolean => {
  if (!workingDays || !workingDays.isActive) {
    return true;
  }
  return resolveShift(dateReference ?? new Date(), workingDays) != null;
};

export const isEstablishmentTemporaryClosed = (
  temporaryClosure: TemporaryClosure | undefined,
  options: { dateReference: Date | undefined },
): boolean => {
  if (!temporaryClosure || temporaryClosure.mode === 'disabled') {
    return false;
  }
  if (temporaryClosure.mode === 'now') {
    return true;
  }
  const start = firestoreDateParser(temporaryClosure.from);
  const end = firestoreDateParser(temporaryClosure.to);

  if (start instanceof Date && end instanceof Date && isValid(start) && isValid(end)) {
    return isWithinInterval(options.dateReference || new Date(), {
      start: startOfDay(start),
      end: endOfDay(end),
    });
  }
  return false;
};

export type ShiftKey = 'primary' | 'secondary' | 'outside';

export type WorkingDayShift = {
  day: Date;
  shift: ShiftKey;
};

export type WorkingDayShiftRange = {
  shift: Exclude<ShiftKey, 'outside'>;
  from: Date;
  to: Date;
};

type DayOfWeek = keyof WorkingDays['daysOfWeekConfig'];

const getDayShiftHours = (dayOfWeek: DayOfWeek, workingDays: WorkingDays) => {
  if (!workingDays.selectedDaysOfWeek.includes(dayOfWeek)) {
    return [];
  }
  const dayConfig = getDayConfig(dayOfWeek, workingDays);

  return [
    { shift: 'primary' as const, hours: dayConfig?.primaryWorkingHours },
    { shift: 'secondary' as const, hours: dayConfig?.secondaryWorkingHours },
  ].filter(
    (entry): entry is { shift: 'primary' | 'secondary'; hours: Required<TimeRange> } =>
      entry.hours?.from != null && entry.hours?.to != null,
  );
};

const previousDayOfWeek = (dayOfWeek: DayOfWeek): DayOfWeek =>
  (dayOfWeek === 1 ? 7 : dayOfWeek - 1) as DayOfWeek;

function resolveShiftDayOffset(
  date: Date,
  workingDays: WorkingDays,
): { shift: Exclude<ShiftKey, 'outside'>; dayOffset: 0 | -1 } | null {
  const dayOfWeek = getISODay(date) as DayOfWeek;
  const timeHHMM = date.getHours() * 100 + date.getMinutes();

  const sameDay = getDayShiftHours(dayOfWeek, workingDays).find(
    ({ hours }) => timeHHMM >= hours.from && timeHHMM < Math.min(hours.to, 2400),
  );

  if (sameDay) {
    return { shift: sameDay.shift, dayOffset: 0 };
  }

  // Check previous day's cross-midnight shifts
  // E.g. a shift 1800-2530 on Friday means open until Saturday 01:30.
  // For a Saturday 01:00 check: timePast24Hours = 2500, which falls within [1800, 2530).
  const timePast24Hours = timeHHMM + 2400;
  const previousDay = getDayShiftHours(previousDayOfWeek(dayOfWeek), workingDays).find(
    ({ hours }) => timePast24Hours >= hours.from && timePast24Hours < hours.to,
  );

  return previousDay ? { shift: previousDay.shift, dayOffset: -1 } : null;
}

export function resolveShift(date: Date, workingDays: WorkingDays): ShiftKey | null {
  return resolveShiftDayOffset(date, workingDays)?.shift ?? null;
}

export function resolveWorkingDay(
  date: Date,
  workingDays: WorkingDays | null | undefined,
): WorkingDayShift {
  const resolved = workingDays?.isActive ? resolveShiftDayOffset(date, workingDays) : null;

  if (!resolved) {
    return { day: startOfDay(date), shift: 'outside' };
  }
  return { day: startOfDay(addDays(date, resolved.dayOffset)), shift: resolved.shift };
}

const hhmmToMinutes = (hhmm: number): number => Math.floor(hhmm / 100) * 60 + (hhmm % 100);

export function getWorkingDayShiftRanges(
  day: Date,
  workingDays: WorkingDays | null | undefined,
): WorkingDayShiftRange[] {
  if (!workingDays?.isActive) {
    return [];
  }
  const dayStart = startOfDay(day);

  return getDayShiftHours(getISODay(dayStart) as DayOfWeek, workingDays).map(
    ({ shift, hours }) => ({
      shift,
      from: addMinutes(dayStart, hhmmToMinutes(hours.from)),
      to: addMinutes(dayStart, hhmmToMinutes(hours.to)),
    }),
  );
}

export function classifyShift(date: Date, workingDays: WorkingDays): ShiftKey {
  return resolveShift(date, workingDays) ?? 'outside';
}

function getDayConfig(dayOfWeek: keyof WorkingDays['daysOfWeekConfig'], workingDays: WorkingDays) {
  return workingDays.customizeHoursByDay
    ? workingDays.daysOfWeekConfig[dayOfWeek]
    : {
        primaryWorkingHours: workingDays.primaryWorkingHours,
        secondaryWorkingHours: workingDays.secondaryWorkingHours,
      };
}

export function hasMultipleShifts(workingDays: WorkingDays | null | undefined): boolean {
  if (!workingDays?.isActive) return false;

  if (workingDays.customizeHoursByDay) {
    return Object.values(workingDays.daysOfWeekConfig).some(
      dayConfig =>
        dayConfig?.primaryWorkingHours?.from != null &&
        dayConfig?.primaryWorkingHours?.to != null &&
        dayConfig?.secondaryWorkingHours?.from != null &&
        dayConfig?.secondaryWorkingHours?.to != null,
    );
  }

  return (
    workingDays.primaryWorkingHours?.from != null &&
    workingDays.primaryWorkingHours?.to != null &&
    workingDays.secondaryWorkingHours?.from != null &&
    workingDays.secondaryWorkingHours?.to != null
  );
}
